/**
 * Wikilink support for rendered documents (GIT_SYNC_PLAN, G6).
 *
 * A mounted wiki writes `[[Page Name]]` / `[[target|label]]`. Rendering keeps
 * the substrate untouched (display-only, like every docView helper):
 *
 *  - {@link remarkWikilinks} is a tiny remark plugin that splits mdast TEXT
 *    nodes on the wikilink pattern and emits link nodes with a `wikilink:`
 *    pseudo-protocol URL. Text inside code spans/fences is never a text node,
 *    so `[[x]]` in code stays literal for free.
 *  - The shared `<Markdown>` renderer resolves those links against the caller's
 *    document list (title first, then filename stem) via
 *    {@link buildWikilinkResolver}; an unresolved target renders as calm muted
 *    text, never a broken link.
 */

export const WIKILINK_PROTOCOL = 'wikilink:';

const WIKILINK_RE = /\[\[([^[\]|]+)(?:\|([^[\]]+))?\]\]/g;

interface MdastNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
}

/** Splits one text node's value into text + wikilink link nodes. */
function splitTextNode(value: string): MdastNode[] | null {
  WIKILINK_RE.lastIndex = 0;
  if (!WIKILINK_RE.test(value)) return null;
  WIKILINK_RE.lastIndex = 0;

  const out: MdastNode[] = [];
  let last = 0;
  for (const match of value.matchAll(WIKILINK_RE)) {
    const index = match.index ?? 0;
    if (index > last) out.push({ type: 'text', value: value.slice(last, index) });
    const target = match[1].trim();
    const label = (match[2] ?? match[1]).trim();
    out.push({
      type: 'link',
      url: `${WIKILINK_PROTOCOL}${encodeURIComponent(target)}`,
      children: [{ type: 'text', value: label }],
    });
    last = index + match[0].length;
  }
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

/** Recursive walk; text under `link` (already a link) and code never splits. */
function walk(node: MdastNode): void {
  if (!node.children) return;
  if (node.type === 'link' || node.type === 'linkReference') return;
  const next: MdastNode[] = [];
  for (const child of node.children) {
    if (child.type === 'text' && typeof child.value === 'string') {
      const split = splitTextNode(child.value);
      if (split) {
        next.push(...split);
        continue;
      }
    }
    walk(child);
    next.push(child);
  }
  node.children = next;
}

/** The remark plugin: `[[target]]` / `[[target|label]]` → `wikilink:` links. */
export function remarkWikilinks() {
  return (tree: unknown) => {
    walk(tree as MdastNode);
  };
}

/** The target of a `wikilink:` href, or null for ordinary hrefs. */
export function wikilinkTarget(href: unknown): string | null {
  if (typeof href !== 'string' || !href.startsWith(WIKILINK_PROTOCOL)) return null;
  try {
    return decodeURIComponent(href.slice(WIKILINK_PROTOCOL.length));
  } catch {
    return href.slice(WIKILINK_PROTOCOL.length);
  }
}

export type WikilinkResolver = (target: string) => string | null;

/** The filename stem of a path: `knowledge/pages/Memex.md` → `memex`. */
function stemOf(path: string): string {
  const base = path.split('/').pop() ?? path;
  return base.replace(/\.md$/i, '').toLowerCase();
}

/**
 * Builds a resolver over the caller's document list: a target matches a
 * document title first, then a filename stem, then a whole path (with or
 * without `.md`) — all case-insensitive. First document wins per key (the
 * list is path-sorted, so resolution is deterministic). Returns the app route
 * for the document, or null when nothing matches.
 */
export function buildWikilinkResolver(
  docs: readonly { path: string; title: string }[]
): WikilinkResolver {
  const byTitle = new Map<string, string>();
  const byStem = new Map<string, string>();
  const byPath = new Map<string, string>();
  for (const doc of docs) {
    const title = doc.title.trim().toLowerCase();
    if (title && !byTitle.has(title)) byTitle.set(title, doc.path);
    const stem = stemOf(doc.path);
    if (!byStem.has(stem)) byStem.set(stem, doc.path);
    byPath.set(doc.path.toLowerCase(), doc.path);
  }
  return (target: string) => {
    const key = target.trim().toLowerCase();
    if (!key) return null;
    const path =
      byTitle.get(key) ?? byStem.get(key) ?? byPath.get(key) ?? byPath.get(`${key}.md`) ?? null;
    return path ? `/docs/${path}` : null;
  };
}
